import React, { useState, useCallback, useMemo } from "react";
import { debounce } from "lodash";
import { getString } from "../utils/localizationHelper";
import { CoreButton, CoreLoader } from "./komodo";
import baseUrl from "../api/config";

const SearchResult = ({ item }) => {
  const [searchInput, setSearchInput] = useState("");
  const [imageErrored, setImageErrored] = useState(false);
  const exampleSuggestions = useMemo(
    () => [{ value: "TNF alpha" }, { value: "VEGF" }, { value: "MA1-744" }],
    [],
  );
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Use QA endpoint for typeahead when running locally
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const typeaheadEndpointTemplate = isLocal
    ? "https://www.thermofisher.com/order/genome-database/typeAheadSearch?keyword={{SEARCH}}&productSubType=antibody_primary"
    : `${baseUrl}/order/genome-database/typeAheadSearch?keyword={{SEARCH}}&productSubType=antibody_primary`;

  // Create a stable search function with proper dependencies
  const performSearch = useCallback(
    async (value) => {
      if (!value || value.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Construct the endpoint using Angular's template logic
        const endpoint = typeaheadEndpointTemplate.replace(
          "{{SEARCH}}",
          encodeURIComponent(value),
        );
        const response = await fetch(endpoint);

        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
          setShowSuggestions(true);
        } else {
          // Fallback to example suggestions if API fails
          setSuggestions(exampleSuggestions);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Typeahead search error:", error);
        // Fallback to example suggestions
        setSuggestions(exampleSuggestions);
        setShowSuggestions(true);
      } finally {
        setLoading(false);
      }
    },
    [typeaheadEndpointTemplate, exampleSuggestions],
  );

  // Debounced search function using useMemo for stable reference
  const debouncedSearch = useMemo(
    () => debounce(performSearch, 300),
    [performSearch],
  );

  // Handle input change with typeahead for primary antibodies
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setSelectedIndex(-1);

    if (item.searchType === "typeahead") {
      if (!value) {
        setSuggestions(exampleSuggestions);
        setShowSuggestions(true);
        setLoading(false);
      } else {
        debouncedSearch(value);
      }
    }
  };

  // Highlight matched text in suggestions
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      text.substring(0, idx) +
      '<span class="tf-match">' +
      text.substring(idx, idx + query.length) +
      "</span>" +
      text.substring(idx + query.length)
    );
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.disabled) return;
    setSearchInput(suggestion.value);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setTimeout(() => {
      handleSearch(suggestion.value);
    }, 100);
  };

  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (
      item.searchType === "typeahead" &&
      showSuggestions &&
      suggestions.length > 0
    ) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            if (!suggestions[selectedIndex].disabled) {
              handleSuggestionClick(suggestions[selectedIndex]);
            }
          } else {
            handleSearch();
          }
          break;
        case "Escape":
          setShowSuggestions(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    } else if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = (searchTerm = searchInput) => {
    if (!searchTerm || !searchTerm.trim()) return;

    // Build the search URL based on the path from master branch
    const searchQuery = item.path.replace(
      "{user-query}",
      encodeURIComponent(searchTerm.trim()),
    );
    const finalQuery = `${searchQuery}${
      searchQuery.includes("?") ? "&" : "?"
    }persistFilters=yes`;

    // Navigate to the search URL (now using dynamic baseUrl)
    const fullUrl = `${baseUrl}/${finalQuery}`;
    window.open(fullUrl, "_self");
  };

  const handleButtonClick = () => {
    // For button type items (antibody-pairs, recombinant-proteins) (now using dynamic baseUrl)
    const fullUrl = `${baseUrl}/${item.path}`;
    window.open(fullUrl, "_self");
  };

  const handleImageError = (e) => {
    if (!imageErrored) {
      setImageErrored(true);
      e.target.src =
        process.env.PUBLIC_URL + "/assets/svg/primary-antibodies.svg";
    }
  };

  if (item.type === "search") {
    return (
      <div className="tf-result-card tf-search-result">
        <div className="tf-result-card-body">
          <div className="tf-result-search-icon">
            <img
              src={process.env.PUBLIC_URL + `/assets/svg/${item.category}.svg`}
              alt={getString(`results.${item.category}`)}
              onError={handleImageError}
            />
          </div>
          <div className="tf-r-p-col">
            <p className="tf-result-search-label">
              {getString(`results.${item.category}`)}
            </p>

            {item.searchType === "typeahead" ? (
              <div className="antibody-search-group">
                <div className="typeahead-embedded">
                  <div
                    className={`tf-dropdown tf-filter tf-elisa-dropdown ${
                      searchInput ? "tf-dropdown-selected" : ""
                    } ${showSuggestions ? "tf-dropdown-open" : ""}`}
                    onClick={() => {
                      if (!showSuggestions) {
                        // If input is empty, show example suggestions
                        if (!searchInput) {
                          setSuggestions(exampleSuggestions);
                        }
                        setShowSuggestions(true);
                      }
                    }}
                  >
                    <div className="tf-dropdown-label">
                      {getString(`results.${item.category}.label`)}
                    </div>
                    <div className="tf-dropdown-placeholder">
                      {getString(`results.${item.category}.placeholder`)}
                    </div>
                    <div className="tf-dropdown-back-arrow">
                      <i className="tf-icon-chevron-left"></i>
                    </div>
                    <div className="tf-dropdown-vwrap">
                      <div className="tf-dropdown-hint"></div>
                      <input
                        className="tf-dropdown-value"
                        value={searchInput}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => {
                          // Only show suggestions, do not trigger API call
                          if (suggestions.length > 0) {
                            setShowSuggestions(true);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowSuggestions(false), 200);
                        }}
                        onMouseDown={(e) => {
                          if (!showSuggestions) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    {showSuggestions &&
                      (searchInput.length >= 2 || suggestions.length > 0) &&
                      !(suggestions.length < 1 && !loading) && (
                        <div className="tf-dropdown-options">
                          {/* Show label only for example suggestions (default or fallback) */}
                          {suggestions.length === 3 &&
                            suggestions.every((s) =>
                              exampleSuggestions.some(
                                (e) => e.value === s.value,
                              ),
                            ) && (
                              <div
                                className="tf-dropdown-examples-label"
                                style={{
                                  fontWeight: "bold",
                                  padding: "8px 16px",
                                  background: "#fff",
                                  textAlign: "left",
                                }}
                              >
                                Example searches
                              </div>
                            )}
                          <ul className="tf-dropdown-examples-list">
                            {suggestions.map((suggestion, index) => (
                              <li
                                key={index}
                                className={`tf-opt ${
                                  index === selectedIndex ? "tf-opt-cursor" : ""
                                }${
                                  suggestion.disabled ? " tf-opt-disabled" : ""
                                }`}
                                data-id={suggestion.id}
                                onClick={(e) => {
                                  if (!suggestion.disabled) {
                                    handleSuggestionClick(suggestion);
                                  }
                                  e.stopPropagation();
                                }}
                                style={{
                                  background:
                                    index === selectedIndex
                                      ? "#e6f0fa"
                                      : "#fff",
                                  fontWeight: "normal",
                                  cursor: suggestion.disabled
                                    ? "not-allowed"
                                    : "pointer",
                                  padding: "8px 16px",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: highlightMatch(
                                    suggestion.value,
                                    searchInput,
                                  ),
                                }}
                              />
                            ))}
                          </ul>
                          {suggestions.length < 1 &&
                            !loading &&
                            searchInput.length >= 2 && (
                              <span className="tf-dropdown-no-options">
                                No matches
                              </span>
                            )}
                          {loading && searchInput.length >= 2 && (
                            <span className="tf-dropdown-no-options">
                              <CoreLoader
                                variant="circular"
                                functionality="indeterminate"
                                size="x-small"
                                information="false"
                                alignment="center"
                              />
                            </span>
                          )}
                        </div>
                      )}
                    <div className="tf-dropdown-caret">
                      {!searchInput && <i className="tf-ui-icon-search"></i>}
                      {searchInput && (
                        <button
                          className="tf-dropdown-clear"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchInput("");
                            setSuggestions([]);
                            setShowSuggestions(false);
                            setSelectedIndex(-1);
                          }}
                          aria-label="Clear search"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          <i className="tf-ui-icon-round-thick-cross"></i>
                        </button>
                      )}
                    </div>
                    <div
                      className="tf-dropdown-error"
                      style={{ display: "none" }}
                    >
                      {/* Error validation message placeholder - matches Angular structure */}
                    </div>
                  </div>
                </div>
                <button
                  className="antibody-search-group-append"
                  onClick={() => handleSearch()}
                  disabled={!searchInput.trim()}
                  style={{
                    cursor: searchInput.trim() ? "pointer" : "not-allowed",
                    background: "#fff",
                    border: "none",
                  }}
                  aria-label="Search"
                >
                  <span className="tf-input-group-text">
                    <i className="tf-ui-icon-search"></i>
                  </span>
                </button>
              </div>
            ) : (
              <div className="tf-text-input-group tf-input-group-appended">
                <input
                  className="tf-text-input-value"
                  value={searchInput}
                  onChange={handleInputChange}
                  placeholder={getString(
                    `results.${item.category}.placeholder`,
                  )}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="tf-input-group-append"
                  onClick={() => handleSearch()}
                  disabled={!searchInput.trim()}
                  aria-label="Search"
                >
                  <span className="tf-input-group-text">
                    <i className="tf-ui-icon-search"></i>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (item.type === "button") {
    return (
      <div className="tf-result-card tf-search-result">
        <div className="tf-result-card-body">
          <div className="tf-result-search-icon">
            <img
              src={process.env.PUBLIC_URL + `/assets/svg/${item.category}.svg`}
              alt={getString(`results.${item.category}`)}
              onError={handleImageError}
            />
          </div>
          <div className="tf-r-p-col">
            <p className="tf-result-search-label">
              {getString(`results.${item.category}`)}
            </p>
            <CoreButton
              onClick={handleButtonClick}
              variant="outline"
              size="small"
              type="button"
              className="tf-btn-block"
            >
              <span slot="content">
                {getString(`results.${item.category}.btn`)}
              </span>
            </CoreButton>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SearchResult;
